import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import WidthContainer from '../../WidthContainer';
import { Heading1Text, ParagraphLargeText, TitleMedium } from '../../Texts';
import Line from '../../Line';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import Button from '../../Button';
import TextInput, { ErrorMessage } from '../../Inputs/Text';
import { useCreatePromotionalGiftMutation } from '../../../redux/api/public/promotional';
import SendingStatus from '../../../views/Gifts/SendingStatus';
import { useSelector } from 'react-redux';
import { selectTokenByNetworkAndSymbol } from '../../../redux/slices/tokenSlice';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Controller } from 'react-hook-form';

const schema = yup
  .object({
    email: yup
      .string()
      .email('Must be a valid email')
      .required('Email is required'),
  })
  .required();

const InnerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 50px;

  @media ${PHONE_MEDIA_QUERY} {
    gap: 24px;
  }
`;

const Container = styled.div`
  min-height: 800px;
  display: flex;
  justify-content: center;
  align-items: center;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    padding: 10px 0px;
  }
`;

const ColorizedSpan = styled.span`
  background-image: linear-gradient(
    90deg,
    ${({ theme }) => theme.text.title} 0%,
    ${({ theme }) => theme.special.purple} 30%,
    ${({ theme }) => theme.primary.primary} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  margin-top: 20px;

  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
  }
`;

const StyledHeading1Text = styled(Heading1Text)`
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 40px;
    line-height: 48px;
  }
`;

const StepContainer = styled.div`
  display: flex;
  gap: 40px;
  padding: 30px 0px;
  align-items: center;

  @media ${PHONE_MEDIA_QUERY} {
    gap: 20px;
    padding: 20px 0px;
  }
`;

const StepsImageContainer = styled.div`
  width: 100%;
  max-width: 400px;

  @media ${PHONE_MEDIA_QUERY} {
    max-width: 75%;
    margin: 0 auto;
  }

  img {
    width: 100%;
    height: auto;
  }
`;

const StepsContentContainer = styled.div`
  margin-top: 50px;

  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 24px;
  }
`;

const getEmailClientUrl = (email: string) => {
  return `mailto:${email}`;
};

const SurveyPromo = () => {
  const [sendingStatus, setSendingStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle');
  const [submittedEmail, setSubmittedEmail] = useState<string>('');
  const [createPromotionalGift, { error }] = useCreatePromotionalGiftMutation();
  const theme = useTheme();
  const token = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });

  const email = watch('email');

  const isSending = sendingStatus === 'sending';
  const isSuccess = sendingStatus === 'success';
  const isError = sendingStatus === 'error';

  const onSubmit = async (data) => {
    try {
      setSendingStatus('sending');
      setSubmittedEmail(data.email);
      await createPromotionalGift({ email: data.email }).unwrap();
      setSendingStatus('success');
    } catch (err) {
      setSendingStatus('error');
    }
  };

  if (isSending || isSuccess) {
    return (
      <WidthContainer>
        <SendingStatus
          email={submittedEmail}
          token={token}
          cardImage='https://deks.xyz/SOL.webp'
          isSuccess={isSuccess}
          isError={isError}
          tokenAmount={'1'}
          usdAmount='1'
          successText={`Your gift has been sent to ${submittedEmail}`}
          successButtonText='Open Email'
          successAction={() => {
            const emailUrl = getEmailClientUrl(submittedEmail);
            window.open(emailUrl, '_blank');
          }}
        />
      </WidthContainer>
    );
  }

  return (
    <>
      <WidthContainer>
        <Container>
          <InnerContainer style={{ alignItems: 'flex-start' }}>
            <div>
              <TitleMedium
                style={{
                  color: theme.text.secondary,
                  width: '100%',
                }}
              >
                GET FREE CRYPTO
              </TitleMedium>
              <StyledHeading1Text
                style={{ color: theme.text.title, width: '100%' }}
              >
                Take Our Survey
              </StyledHeading1Text>
              <StyledHeading1Text>
                <ColorizedSpan>And Get Rewarded</ColorizedSpan>
              </StyledHeading1Text>
            </div>
            <StepsImageContainer>
              <img
                src='/simpleStepsToGiftCrypto.webp'
                alt='Steps to gift crypto'
              />
            </StepsImageContainer>
          </InnerContainer>
          <InnerContainer>
            <StepsContentContainer>
              <Line />
              <StepContainer>
                <StyledHeading1Text style={{ color: theme.primary.primary }}>
                  1
                </StyledHeading1Text>
                <ParagraphLargeText>
                  <b>Quick Survey.</b> Share your thoughts in our 2-minute
                  digital gifting survey.
                </ParagraphLargeText>
              </StepContainer>
              <Line />
              <StepContainer>
                <StyledHeading1Text style={{ color: theme.primary.primary }}>
                  2
                </StyledHeading1Text>
                <ParagraphLargeText>
                  <b>Get Rewarded.</b> Receive free Solana (SOL) for completing
                  the survey.
                </ParagraphLargeText>
              </StepContainer>
              <Line />
              <StepContainer>
                <StyledHeading1Text style={{ color: theme.primary.primary }}>
                  3
                </StyledHeading1Text>
                <ParagraphLargeText>
                  <b>Help Shape the Future.</b> Your input will help
                  revolutionize digital gifting.
                </ParagraphLargeText>
              </StepContainer>
              <Line />
              <InputContainer>
                <Controller
                  name='email'
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      label='Email'
                      placeholder='Your email'
                      type='email'
                      dark={false}
                      error={errors?.email?.message}
                      {...field}
                    />
                  )}
                />
                <Button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSending || !email}
                  $variant='primary'
                  fullWidth={true}
                >
                  {isSending ? 'Submitting...' : 'Claim Reward'}
                </Button>
                {isError && <ErrorMessage>{error?.data?.error}</ErrorMessage>}
              </InputContainer>
            </StepsContentContainer>
          </InnerContainer>
        </Container>
      </WidthContainer>
    </>
  );
};

export default SurveyPromo;
